import { Field, TextInput, TextArea, Select } from './Field.jsx';

/**
 * Renders the call script returned by the API.
 *
 * The hierarchy on screen matches the hierarchy on the phone:
 *   1. `script`   - what the teleprospector reads out, big and quoted
 *   2. the input  - where the answer goes
 *   3. `guidance` - internal coaching, small and muted, never read aloud
 *
 * `info` questions have a script but no answer (opening hook, closing pitch).
 * There is no hard-coded question here: the backend definition drives everything.
 */
const isVisible = (question, answers) => {
  if (!question.showIf) return true;
  const value = answers[question.showIf.question];
  const expected = question.showIf.in ?? [];
  if (Array.isArray(value)) return value.some((item) => expected.includes(item));
  return expected.includes(value);
};

const QuestionControl = ({ question, value, onChange }) => {
  const id = `q-${question.key}`;

  switch (question.type) {
    case 'textarea':
      return <TextArea id={id} value={value} onChange={onChange} rows={5} />;

    case 'email':
      return <TextInput id={id} type="email" value={value} onChange={onChange} />;

    case 'phone':
      return <TextInput id={id} type="tel" value={value} onChange={onChange} />;

    case 'number':
      return <TextInput id={id} type="number" value={value} onChange={(v) => onChange(v === '' ? '' : Number(v))} />;

    case 'date':
      return <TextInput id={id} type="date" value={value} onChange={onChange} />;

    case 'select':
      return <Select id={id} value={value} onChange={onChange} options={question.options ?? []} placeholder="-" />;

    case 'radio':
      return (
        <div className="options options--radio">
          {(question.options ?? []).map((option) => (
            <label key={option.value} className={value === option.value ? 'option option--checked' : 'option'}>
              <input
                type="radio"
                name={id}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );

    case 'multiselect': {
      const selected = Array.isArray(value) ? value : [];
      const toggle = (optionValue) =>
        onChange(
          selected.includes(optionValue)
            ? selected.filter((item) => item !== optionValue)
            : [...selected, optionValue],
        );
      return (
        <div className="options options--checkbox">
          {(question.options ?? []).map((option) => (
            <label
              key={option.value}
              className={selected.includes(option.value) ? 'option option--checked' : 'option'}
            >
              <input type="checkbox" checked={selected.includes(option.value)} onChange={() => toggle(option.value)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'boolean':
      return (
        <label className={value ? 'switch switch--on' : 'switch'}>
          <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
          <span className="switch__track" aria-hidden="true" />
        </label>
      );

    case 'scale': {
      const min = question.min ?? 1;
      const max = question.max ?? 10;
      return (
        <div className="scale">
          <input
            id={id}
            type="range"
            min={min}
            max={max}
            value={value ?? min}
            onChange={(event) => onChange(Number(event.target.value))}
          />
          <output className="scale__value">{value ?? '-'}</output>
        </div>
      );
    }

    default:
      return <TextInput id={id} value={value} onChange={onChange} />;
  }
};

/** The sentence to say. This is the most important thing on the screen. */
const Script = ({ text }) => (
  <blockquote className="script">
    <span className="script__quote" aria-hidden="true">&laquo;</span>
    <p className="script__text">{text}</p>
    <span className="script__quote" aria-hidden="true">&raquo;</span>
  </blockquote>
);

const Guidance = ({ text }) => (
  <p className="guidance">
    <span className="guidance__tag" aria-hidden="true">i</span>
    <span>{text}</span>
  </p>
);

const DynamicForm = ({ definition, answers, onChange, errors = {}, disabled = false }) => {
  if (!definition) return null;

  return (
    <div className={disabled ? 'dynamic-form dynamic-form--disabled' : 'dynamic-form'}>
      {definition.sections.map((section) => {
        const visibleQuestions = section.questions.filter((question) => isVisible(question, answers));
        if (!visibleQuestions.length) return null;

        return (
          <fieldset key={section.key} className="form-section" disabled={disabled}>
            <legend className="form-section__title">{section.title}</legend>

            <div className="form-section__list">
              {visibleQuestions.map((question) => {
                // Read-aloud only: a pitch or a transition, nothing to record.
                if (question.type === 'info') {
                  return (
                    <div key={question.key} className="question question--info">
                      <p className="question__label">{question.label}</p>
                      {question.script ? <Script text={question.script} /> : null}
                      {question.guidance ? <Guidance text={question.guidance} /> : null}
                    </div>
                  );
                }

                return (
                  <div key={question.key} className="question">
                    {question.script ? <Script text={question.script} /> : null}

                    <Field
                      label={question.label}
                      htmlFor={`q-${question.key}`}
                      required={question.required}
                      error={errors[question.key]}
                    >
                      <QuestionControl
                        question={question}
                        value={answers[question.key]}
                        onChange={(value) => onChange(question.key, value)}
                      />
                    </Field>

                    {question.guidance ? <Guidance text={question.guidance} /> : null}
                  </div>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
};

export default DynamicForm;
